import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TextField,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { useNavItems } from '../../../hooks';
import { useDesignatedOperators } from '../../Profile/hooks/useDesignatedOperators';
import { useUser } from '../../../hooks/useUser';
import { useCn61Reports, exportCn61Data, fetchCn61ReimData, Cn61ExportPayload, Cn61ExportParams, Cn61ReimParams } from './hooks';
import { CN61Table, CN61HtmlReportTable } from './components';
import NotificationSnackbar from '../../../common/components/Snackbar/Snackbar';
import { NoDataFound } from '../../../common/components/NoDataFound';
import {
  MainContent,
  ReportContainer,
  SectionTitle,
  CheckboxGroup,
  ButtonContainer,
  FilterSection,
  SelectContainer,
  StyledSegmentedControl,
  StyledSegmentButton,
  ClearResultsButton,
  ExportButton,
} from './CN61CompoundRateReports.styled';

interface DirectionOption {
  id: string;
  label: string;
}

interface CN61ReportFilters {
  ppoValue: string;
  direction: string;
  year: string;
}

// Generate year options from 2008 to current year
const generateYearOptions = () => {
  const options: { value: string; label: string }[] = [];
  const currentYear = new Date().getFullYear();

  for (let year = currentYear; year >= 2008; year--) {
    options.push({
      value: year.toString(),
      label: year.toString(),
    });
  }

  return options;
};

function CN61ReimsPage() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter states - Initialize from URL params if available
  const [filters, setFilters] = useState<CN61ReportFilters>(() => {
    const ppo = searchParams.get('ppo') || '';
    const year = searchParams.get('year') || '';
    const direction = searchParams.get('direction') || 'Inbound';
    
    return {
      ppoValue: ppo,
      direction: direction,
      year: year,
    };
  });

  // Check if we came from a redirect (has URL params)
  const isFromRedirect = useMemo(() => {
    return !!(searchParams.get('ppo') && searchParams.get('year'));
  }, [searchParams]);

  // Check if we have CRR data from the URL params
  const crrDataFromUrl = useMemo(() => {
    const crrDataParam = searchParams.get('crrData');
    if (crrDataParam) {
      try {
        return JSON.parse(decodeURIComponent(crrDataParam));
      } catch (error) {
        console.error('Failed to parse CRR data from URL:', error);
        return null;
      }
    }
    return null;
  }, [searchParams]);

  // Check if we have CN61 extra data from URL params (from CRR CSV upload)
  const cn61ExtraDataFromUrl = useMemo(() => {
    const cn61ExtraParam = searchParams.get('cn61ExtraData');
    if (cn61ExtraParam) {
      try {
        return JSON.parse(decodeURIComponent(cn61ExtraParam));
      } catch (error) {
        console.error('Failed to parse CN61 extra data from URL:', error);
        return null;
      }
    }
    return null;
  }, [searchParams]);

  // Check if we came from CRR page (has CRR data)
  const isFromCrrPage = useMemo(() => {
    return !!(crrDataFromUrl && searchParams.get('ppo') && searchParams.get('year'));
  }, [crrDataFromUrl, searchParams]);

  // Results state
  const [results, setResults] = useState<any[] | null>(null);
  const [noDataFound, setNoDataFound] = useState<boolean>(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);
  
  // Track if user has made any TD rates request
  const [hasTdRatesData, setHasTdRatesData] = useState<boolean>(false);
  
  // CN61 data state - will store user input values for export
  const [cn61InputData, setCn61InputData] = useState<any>(null);
  
  // CN61 HTML report data state
  const [cn61ReportData, setCn61ReportData] = useState<any[] | null>(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'warning' | 'info' | 'success',
  });

  // Alert state for 400 errors
  const [showIncompleteDataAlert, setShowIncompleteDataAlert] = useState<boolean>(false);

  // Currency state
  const [currency, setCurrency] = useState<string>('EUR');

  // Fetch designated operators for PPO dropdown
  const {
    data: designatedOperatorsData,
    isLoading: isPpoLoading,
    error: ppoError,
  } = useDesignatedOperators({ size: 300 });

  // Get current user info for CSV functionality
  const { data: userInfo } = useUser();

  // CN61 Reports queries for REIM endpoint
  const {
    data: cn61ReimData,
    isLoading: isCn61ReimLoading,
    error: cn61ReimError,
    refetch: refetchCn61Reim,
  } = useCn61Reports(
    {
      ppo: filters.ppoValue || '',
      year: filters.year ? parseInt(filters.year) : 0,
      direction:
        filters.direction === 'All'
          ? 'ALL'
          : (filters.direction.toUpperCase() as 'INBOUND' | 'OUTBOUND'),
      includeCrr: false, // Always false for this page
    },
    'reim',
    false // Disabled by default, will be enabled when button is clicked
  );

  // Generate year options
  const yearOptions = useMemo(() => generateYearOptions(), []);

  // Direction filter options
  const directionOptions: DirectionOption[] = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
    { id: 'All', label: 'All' },
  ];

  // Map the designated operators to PPO options
  const ppoOptions = React.useMemo(() => {
    const options: { id: string; label: string; hasLicense?: boolean }[] = [];

    if (designatedOperatorsData?.content) {
      designatedOperatorsData.content
        .filter((operator) => !operator.disabled)
        .forEach((operator) => {
          options.push({
            id: operator.name,
            label: operator.name,
            hasLicense: operator.longName !== null,
          });
        });
    }

    return options;
  }, [designatedOperatorsData]);

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/cn61-crr');
  };

  const handleDirectionChange = (id: string) => {
    setFilters((prev) => ({ ...prev, direction: id }));
  };

  const handlePpoChange = (value: string) => {
    setFilters((prev) => ({ ...prev, ppoValue: value }));
  };

  const handleYearChange = (value: string) => {
    setFilters((prev) => ({ ...prev, year: value }));
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const showSnackbar = (
    message: string,
    severity: 'error' | 'warning' | 'info' | 'success' = 'error'
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleReset = () => {
    setFilters({
      ppoValue: '',
      direction: 'Inbound',
      year: '',
    });
    setResults(null);
    setNoDataFound(false);
    setCn61ReportData(null);
    setHasTdRatesData(false);
    setShowIncompleteDataAlert(false);
    setSearchParams(new URLSearchParams());
  };

  // Validate that all present fields are filled with exactly 3 decimals
  const threeDecimalsRegex = useMemo(() => /^\d+\.\d{3}$/, []);
  
  const isValueComplete = useCallback((val: unknown) => {
    if (val === null || val === undefined || val === '') return false;
    return threeDecimalsRegex.test(String(val));
  }, [threeDecimalsRegex]);

  // Format value to 3 decimals (e.g., 2 -> "2.000")
  const formatToThreeDecimals = useCallback((value: number | string | null | undefined): string | null => {
    if (value === null || value === undefined || value === '') return null;
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return null;
    return numValue.toFixed(3);
  }, []);

  // Validate CN61 direction data - all fields must have exactly 3 decimals
  const validateCn61DirectionData = useCallback((cn61Input: any) => {
    if (!cn61Input) return false;
    
    let allFieldsValid = true;
    
    Object.values(cn61Input).forEach((entry: any) => {
      if (!entry || typeof entry !== 'object') return;
      
      if (entry.weight) {
        if (!isValueComplete(entry.weight.priority) || !isValueComplete(entry.weight.nonPriority)) {
          allFieldsValid = false;
        }
      }
      
      if (entry.items) {
        if (!isValueComplete(entry.items.priority) || !isValueComplete(entry.items.nonPriority)) {
          allFieldsValid = false;
        }
      }
    });
    
    return allFieldsValid;
  }, [isValueComplete]);

  // Validate CN61 data - all fields must have exactly 3 decimals
  const validateCN61Data = useCallback((data: any) => {
    if (!data || !data.request) return false;
    
    const { inboundCn61Input, outboundCn61Input } = data.request;
    
    // Check if at least one direction has data
    if (!inboundCn61Input && !outboundCn61Input) return false;
    
    // Validate inbound data if present
    if (inboundCn61Input) {
      const hasValidInbound = validateCn61DirectionData(inboundCn61Input);
      if (!hasValidInbound) return false;
    }
    
    // Validate outbound data if present
    if (outboundCn61Input) {
      const hasValidOutbound = validateCn61DirectionData(outboundCn61Input);
      if (!hasValidOutbound) return false;
    }
    
    return true;
  }, [validateCn61DirectionData]);

  // Export CN61 data to /cn61/cn61 endpoint using the hook
  const exportCN61Data = async (format: 'html' | 'pdf' | 'xls') => {
    if (!cn61InputData) {
      showSnackbar('No CN61 data available for export');
      return;
    }

    try {
      const exportPayload: Cn61ExportPayload = {
        currency: currency,
        inboundCn61Input: cn61InputData.request.inboundCn61Input,
        outboundCn61Input: cn61InputData.request.outboundCn61Input
      };

      const exportParams: Cn61ExportParams = {
        flow: 'cn61',
        designatedOperator: filters.ppoValue,
        year: filters.year,
        direction: filters.direction.toUpperCase(),
        formatType: format.toLowerCase()
      };

      const response = await exportCn61Data(exportPayload, exportParams);

      if (response.ok) {
        // Handle successful export based on format
        if (format === 'html') {
          try {
            const jsonData = await response.json();
            // Handle both inbound and outbound data with direction information
            const reportData = [];
            const balance = (jsonData.balanceFormatted ?? jsonData.balance) ?? null;
            if (jsonData.reports?.inbound) {
              reportData.push({
                direction: 'inbound',
                data: Array.isArray(jsonData.reports?.inbound) ? jsonData.reports?.inbound : [jsonData.reports?.inbound],
                balance,
              });
            }
            if (jsonData.reports?.outbound) {
              reportData.push({
                direction: 'outbound', 
                data: Array.isArray(jsonData.reports?.outbound) ? jsonData.reports?.outbound : [jsonData.reports?.outbound],
                balance,
              });
            }
            // Fallback for old format
            if (!jsonData.reports?.inbound && !jsonData.reports?.outbound && jsonData.reports) {
              reportData.push({
                direction: 'unknown',
                data: Array.isArray(jsonData) ? jsonData : [jsonData],
                balance,
              });
            }
            setCn61ReportData(reportData);
            showSnackbar(`${format.toUpperCase()} report loaded successfully!`, 'success');
          } catch (jsonError) {
            // Try to get response as text to see what we actually received
            const textData = await response.text();
            showSnackbar('Failed to parse response data', 'error');
            return;
          }
        } else {
          // For PDF/XLS, trigger download
          const blob = await response.blob();
          
          // Validate blob size
          if (blob.size === 0) {
            showSnackbar('Received empty file from server', 'error');
            return;
          }
          
          // Extract filename from Content-Disposition header
          const contentDisposition = response.headers.get('Content-Disposition');
          let filename = `cn61-reims-report-${filters.ppoValue}-${filters.year}.${format.toLowerCase()}`;
          
          if (contentDisposition) {
            const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
            if (filenameMatch && filenameMatch[2]) {
              filename = decodeURIComponent(filenameMatch[2]);
            }
          }
          
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          
          // Cleanup after a short delay
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 100);
          
          showSnackbar(`${format.toUpperCase()} report exported successfully!`, 'success');
        }
      } else {
        // Handle specific error cases
        if (response.status === 400) {
          setShowIncompleteDataAlert(true);
         
          return;
        }
        
        // Try to get error message from response
        try {
          const errorText = await response.text();
          showSnackbar(`Export failed: ${errorText}`, 'error');
        } catch (e) {
          showSnackbar(`Export failed with status: ${response.status} ${response.statusText}`, 'error');
        }
        return;
      }
    } catch (error) {
      showSnackbar('An error occurred while exporting the report', 'error');
    }
  };

  const handleExport = async (format: 'html' | 'pdf' | 'xls') => {
    // Validate required fields
    if (!filters.year) {
      showSnackbar('Please select a Year');
      return;
    }

    if (!filters.ppoValue) {
      showSnackbar('Please select a PPO');
      return;
    }

    // Validate CN61 data
    if (!cn61InputData) {
      showSnackbar('Please load CN61 data first by clicking "Enter TD Rates"');
      return;
    }

    const hasValidCN61Data = validateCN61Data(cn61InputData);
    if (!hasValidCN61Data) {
      showSnackbar('Please fill all CN61 TD rates with exactly 3 decimals before exporting');
      return;
    }

    // Show loading state
    setIsGeneratingReport(true);

    try {
      // For regular CN61 export (no CRR), use CN61 endpoint with user input data
      await exportCN61Data(format);
    } catch (error) {
      showSnackbar('An error occurred while generating the report', 'error');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleEnterTdRates = useCallback(async () => {
    // Validate required fields
    if (!filters.year) {
      showSnackbar('Please select a Year');
      return;
    }

    if (!filters.ppoValue) {
      showSnackbar('Please select a PPO');
      return;
    }

    // Show loading state
    setIsGeneratingReport(true);

    try {
      // Call the /cn61/reim/{designatedOperator}/{year}/{direction} endpoint using the new API
      const reimParams: Cn61ReimParams = {
        designatedOperator: filters.ppoValue,
        year: filters.year,
        direction: filters.direction.toUpperCase()
      };

      const response = await fetchCn61ReimData(reimParams);

      if (response.ok) {
        try {
          const jsonData = await response.json();
          
          // Apply CRR data if coming from CRR page
          let finalData = jsonData;
          if (isFromCrrPage && crrDataFromUrl) {
            // Inline CRR data application to avoid dependency issues
            const updatedCn61Data = { ...jsonData };

            // Apply CRR data to inbound section if it exists
            if (updatedCn61Data.request.inboundCn61Input && crrDataFromUrl.inbound) {
              const inboundInput = updatedCn61Data.request.inboundCn61Input;

              // Apply P format values - priority and non-priority from separate CRR totals
              if (inboundInput.formatP) {
                if (inboundInput.formatP.weight) {
                  inboundInput.formatP.weight.priority = formatToThreeDecimals(crrDataFromUrl.inbound.formatP.priority.kg) ?? inboundInput.formatP.weight.priority;
                  inboundInput.formatP.weight.nonPriority = formatToThreeDecimals(crrDataFromUrl.inbound.formatP.nonPriority.kg) ?? inboundInput.formatP.weight.nonPriority;
                }
                if (inboundInput.formatP.items) {
                  inboundInput.formatP.items.priority = formatToThreeDecimals(crrDataFromUrl.inbound.formatP.priority.item) ?? inboundInput.formatP.items.priority;
                  inboundInput.formatP.items.nonPriority = formatToThreeDecimals(crrDataFromUrl.inbound.formatP.nonPriority.item) ?? inboundInput.formatP.items.nonPriority;
                }
              }

              // Apply G format values - priority and non-priority from separate CRR totals
              if (inboundInput.formatG) {
                if (inboundInput.formatG.weight) {
                  inboundInput.formatG.weight.priority = formatToThreeDecimals(crrDataFromUrl.inbound.formatG.priority.kg) ?? inboundInput.formatG.weight.priority;
                  inboundInput.formatG.weight.nonPriority = formatToThreeDecimals(crrDataFromUrl.inbound.formatG.nonPriority.kg) ?? inboundInput.formatG.weight.nonPriority;
                }
                if (inboundInput.formatG.items) {
                  inboundInput.formatG.items.priority = formatToThreeDecimals(crrDataFromUrl.inbound.formatG.priority.item) ?? inboundInput.formatG.items.priority;
                  inboundInput.formatG.items.nonPriority = formatToThreeDecimals(crrDataFromUrl.inbound.formatG.nonPriority.item) ?? inboundInput.formatG.items.nonPriority;
                }
              }

              // Apply E format values - priority and non-priority from separate CRR totals
              if (inboundInput.formatE) {
                if (inboundInput.formatE.weight) {
                  inboundInput.formatE.weight.priority = formatToThreeDecimals(crrDataFromUrl.inbound.formatE.priority.kg) ?? inboundInput.formatE.weight.priority;
                  inboundInput.formatE.weight.nonPriority = formatToThreeDecimals(crrDataFromUrl.inbound.formatE.nonPriority.kg) ?? inboundInput.formatE.weight.nonPriority;
                }
                if (inboundInput.formatE.items) {
                  inboundInput.formatE.items.priority = formatToThreeDecimals(crrDataFromUrl.inbound.formatE.priority.item) ?? inboundInput.formatE.items.priority;
                  inboundInput.formatE.items.nonPriority = formatToThreeDecimals(crrDataFromUrl.inbound.formatE.nonPriority.item) ?? inboundInput.formatE.items.nonPriority;
                }
              }
            }

            // Apply CRR data to outbound section if it exists
            if (updatedCn61Data.request.outboundCn61Input && crrDataFromUrl.outbound) {
              const outboundInput = updatedCn61Data.request.outboundCn61Input;

              // Apply P format values - priority and non-priority from separate CRR totals
              if (outboundInput.formatP) {
                if (outboundInput.formatP.weight) {
                  outboundInput.formatP.weight.priority = formatToThreeDecimals(crrDataFromUrl.outbound.formatP.priority.kg) ?? outboundInput.formatP.weight.priority;
                  outboundInput.formatP.weight.nonPriority = formatToThreeDecimals(crrDataFromUrl.outbound.formatP.nonPriority.kg) ?? outboundInput.formatP.weight.nonPriority;
                }
                if (outboundInput.formatP.items) {
                  outboundInput.formatP.items.priority = formatToThreeDecimals(crrDataFromUrl.outbound.formatP.priority.item) ?? outboundInput.formatP.items.priority;
                  outboundInput.formatP.items.nonPriority = formatToThreeDecimals(crrDataFromUrl.outbound.formatP.nonPriority.item) ?? outboundInput.formatP.items.nonPriority;
                }
              }

              // Apply G format values - priority and non-priority from separate CRR totals
              if (outboundInput.formatG) {
                if (outboundInput.formatG.weight) {
                  outboundInput.formatG.weight.priority = formatToThreeDecimals(crrDataFromUrl.outbound.formatG.priority.kg) ?? outboundInput.formatG.weight.priority;
                  outboundInput.formatG.weight.nonPriority = formatToThreeDecimals(crrDataFromUrl.outbound.formatG.nonPriority.kg) ?? outboundInput.formatG.weight.nonPriority;
                }
                if (outboundInput.formatG.items) {
                  outboundInput.formatG.items.priority = formatToThreeDecimals(crrDataFromUrl.outbound.formatG.priority.item) ?? outboundInput.formatG.items.priority;
                  outboundInput.formatG.items.nonPriority = formatToThreeDecimals(crrDataFromUrl.outbound.formatG.nonPriority.item) ?? outboundInput.formatG.items.nonPriority;
                }
              }

              // Apply E format values - priority and non-priority from separate CRR totals
              if (outboundInput.formatE) {
                if (outboundInput.formatE.weight) {
                  outboundInput.formatE.weight.priority = formatToThreeDecimals(crrDataFromUrl.outbound.formatE.priority.kg) ?? outboundInput.formatE.weight.priority;
                  outboundInput.formatE.weight.nonPriority = formatToThreeDecimals(crrDataFromUrl.outbound.formatE.nonPriority.kg) ?? outboundInput.formatE.weight.nonPriority;
                }
                if (outboundInput.formatE.items) {
                  outboundInput.formatE.items.priority = formatToThreeDecimals(crrDataFromUrl.outbound.formatE.priority.item) ?? outboundInput.formatE.items.priority;
                  outboundInput.formatE.items.nonPriority = formatToThreeDecimals(crrDataFromUrl.outbound.formatE.nonPriority.item) ?? outboundInput.formatE.items.nonPriority;
                }
              }
            }

            finalData = updatedCn61Data;
          }

          // Apply CN61 extra data from CRR CSV upload (Postcards, Registered, Insured, Express, M-Bags)
          if (cn61ExtraDataFromUrl) {
            // Apply inbound CN61 extra data
            if (cn61ExtraDataFromUrl.inboundCn61Input && finalData.request?.inboundCn61Input) {
              const inboundInput = finalData.request.inboundCn61Input;
              const extraInbound = cn61ExtraDataFromUrl.inboundCn61Input;

              // Apply each extra field (postcards, registered, insured, express, mbags)
              ['postcards', 'registered', 'insured', 'express', 'mbags'].forEach(fieldKey => {
                if (extraInbound[fieldKey]) {
                  // Initialize the field if it doesn't exist in the API response
                  if (!inboundInput[fieldKey]) {
                    inboundInput[fieldKey] = {};
                  }
                  // Apply weight data if present
                  if (extraInbound[fieldKey].weight) {
                    if (!inboundInput[fieldKey].weight) {
                      inboundInput[fieldKey].weight = {};
                    }
                    if (extraInbound[fieldKey].weight.priority) {
                      inboundInput[fieldKey].weight.priority = extraInbound[fieldKey].weight.priority;
                    }
                    if (extraInbound[fieldKey].weight.nonPriority) {
                      inboundInput[fieldKey].weight.nonPriority = extraInbound[fieldKey].weight.nonPriority;
                    }
                  }
                  // Apply items data if present
                  if (extraInbound[fieldKey].items) {
                    if (!inboundInput[fieldKey].items) {
                      inboundInput[fieldKey].items = {};
                    }
                    if (extraInbound[fieldKey].items.priority) {
                      inboundInput[fieldKey].items.priority = extraInbound[fieldKey].items.priority;
                    }
                    if (extraInbound[fieldKey].items.nonPriority) {
                      inboundInput[fieldKey].items.nonPriority = extraInbound[fieldKey].items.nonPriority;
                    }
                  }
                }
              });
            }

            // Apply outbound CN61 extra data
            if (cn61ExtraDataFromUrl.outboundCn61Input && finalData.request?.outboundCn61Input) {
              const outboundInput = finalData.request.outboundCn61Input;
              const extraOutbound = cn61ExtraDataFromUrl.outboundCn61Input;

              // Apply each extra field (postcards, registered, insured, express, mbags)
              ['postcards', 'registered', 'insured', 'express', 'mbags'].forEach(fieldKey => {
                if (extraOutbound[fieldKey]) {
                  // Initialize the field if it doesn't exist in the API response
                  if (!outboundInput[fieldKey]) {
                    outboundInput[fieldKey] = {};
                  }
                  // Apply weight data if present
                  if (extraOutbound[fieldKey].weight) {
                    if (!outboundInput[fieldKey].weight) {
                      outboundInput[fieldKey].weight = {};
                    }
                    if (extraOutbound[fieldKey].weight.priority) {
                      outboundInput[fieldKey].weight.priority = extraOutbound[fieldKey].weight.priority;
                    }
                    if (extraOutbound[fieldKey].weight.nonPriority) {
                      outboundInput[fieldKey].weight.nonPriority = extraOutbound[fieldKey].weight.nonPriority;
                    }
                  }
                  // Apply items data if present
                  if (extraOutbound[fieldKey].items) {
                    if (!outboundInput[fieldKey].items) {
                      outboundInput[fieldKey].items = {};
                    }
                    if (extraOutbound[fieldKey].items.priority) {
                      outboundInput[fieldKey].items.priority = extraOutbound[fieldKey].items.priority;
                    }
                    if (extraOutbound[fieldKey].items.nonPriority) {
                      outboundInput[fieldKey].items.nonPriority = extraOutbound[fieldKey].items.nonPriority;
                    }
                  }
                }
              });
            }
          }

          // Show appropriate success message
          if (isFromCrrPage && crrDataFromUrl) {
            const hasExtraData = cn61ExtraDataFromUrl && 
              (Object.keys(cn61ExtraDataFromUrl.inboundCn61Input || {}).length > 0 ||
               Object.keys(cn61ExtraDataFromUrl.outboundCn61Input || {}).length > 0);
            
            if (hasExtraData) {
              showSnackbar('CN61 REIMS/Interconnect data loaded with CRR totals and extra fields!', 'success');
            } else {
              showSnackbar('CN61 REIMS/Interconnect data loaded and pre-populated with CRR totals!', 'success');
            }
          } else {
            showSnackbar('CN61 REIMS/Interconnect data loaded successfully!', 'success');
          }
          
          // Store the CN61 input data for editing and future exports
          setCn61InputData(finalData);
          // Initialize currency from data if available
          if (finalData.request?.currency) {
            setCurrency(finalData.request.currency);
          }
          // Also store in results for display using CN61Table
          setResults([finalData]);
          setNoDataFound(false);
          setHasTdRatesData(true);
        } catch (jsonError) {
          showSnackbar('Failed to parse response data', 'error');
          return;
        }
      } else {
        if (response.status === 404) {
          setResults(null);
          setNoDataFound(true);
          showSnackbar('No data found for the selected criteria', 'info');
        } else if (response.status === 400) {
          setResults(null);
          setNoDataFound(false);
          setShowIncompleteDataAlert(true);
        } else {
          const errorText = await response.text();
          showSnackbar(`Failed to load data: ${errorText}`, 'error');
        }
      }
    } catch (error) {
      showSnackbar('An error occurred while loading the data', 'error');
    } finally {
      setIsGeneratingReport(false);
    }
  }, [filters.year, filters.ppoValue, filters.direction, isFromCrrPage, crrDataFromUrl, cn61ExtraDataFromUrl, showSnackbar, formatToThreeDecimals]);

  const clearResults = useCallback(() => {
    setResults(null);
    setNoDataFound(false);
    setCn61ReportData(null);
    setHasTdRatesData(false);
    setShowIncompleteDataAlert(false);
    setCurrency('EUR'); // Reset currency to default
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Handle CN61 data changes
  const handleCN61DataChange = useCallback((updatedData: any) => {
    setCn61InputData(updatedData);
  }, []);

  // Handle CN61 success messages
  const handleCN61Success = useCallback((message: string) => {
    showSnackbar(message, 'success');
  }, [showSnackbar]);


  // Manual retry function
  const handleRetry = () => {
    if (retryCount < 3) {
      setRetryCount(prev => prev + 1);
      handleEnterTdRates();
    } else {
      showSnackbar('Maximum retry attempts reached. Please check your connection and try again later.', 'error');
    }
  };

  // Track retry attempts
  const [retryCount, setRetryCount] = useState<number>(0);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState<boolean>(false);

  // Auto-load data when coming from redirect or CRR page
  useEffect(() => {
    if (isFromRedirect && filters.ppoValue && filters.year && !hasTdRatesData && !isGeneratingReport && !hasAttemptedLoad && retryCount < 3) {
      // Make the API request first (both regular redirect and CRR page)
      setHasAttemptedLoad(true);
      setRetryCount(prev => prev + 1);
      handleEnterTdRates();
    }
  }, [isFromRedirect, filters.ppoValue, filters.year, hasTdRatesData, isGeneratingReport, hasAttemptedLoad, retryCount, handleEnterTdRates]);

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column' }}
      data-testid='cn61-reims-page-container'
    >
      <Grid container data-testid="cn61-reims-page-grid-container">
        {/* First Row - SectionBar */}
        <Grid item xs={12} data-testid="cn61-reims-page-section-bar-container">
          <SectionBar
            title='CN61 Interconnect'
            subtitle='IPC TD Accounting'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            onSubtitleClick={handleSubtitleClick}
            data-testid='cn61-reims-page-section-bar'
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }} data-testid="cn61-reims-page-main-row">
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }} data-testid="cn61-reims-page-sidebar-container">
              <Sidebar
                isOpen={sidebarOpen}
                onToggle={handleSidebarToggle}
                navItems={navItems}
                data-testid='cn61-reims-page-sidebar'
              />
            </Box>
          )}

          {/* Main Content */}
          <MainContent
            sidebarOpen={sidebarOpen}
            data-testid='cn61-reims-page-main-content'
          >
            <ReportContainer data-testid='cn61-reims-page-content'>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
                data-testid='cn61-reims-page-header'
              >
                <Box data-testid="cn61-reims-page-title-section">
                  <Typography
                    variant='h4'
                    sx={{ fontWeight: 600, color: '#063142' }}
                    data-testid='cn61-reims-page-title'
                  >
                    CN61 Interconnect
                  </Typography>
                  {isFromCrrPage && (
                    <Typography
                      variant='body2'
                      sx={{ 
                        color: '#00ACE7', 
                        fontWeight: 500, 
                        mt: 0.5,
                        fontSize: '13px'
                      }}
                      data-testid="cn61-reims-page-crr-populated-text"
                    >
                      Pre-populated with CRR totals
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{ display: 'flex', gap: 1 }}
                  data-testid='cn61-reims-page-header-actions'
                >
                  <Button
                    variant='text'
                    onClick={() => {
                      if (isFromCrrPage) {
                        // Go back to CRR page with the same parameters
                        const backParams = new URLSearchParams({
                          ppo: filters.ppoValue,
                          year: filters.year,
                          direction: filters.direction,
                        });
                        navigate(`/cn61-compound-rate-reports/crr?${backParams.toString()}`);
                      } else {
                        navigate('/cn61-crr');
                      }
                    }}
                    sx={{
                      color: '#00ACE7',
                      fontSize: '12px',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#E3F7FE',
                      },
                    }}
                    data-testid="cn61-reims-page-back-btn"
                  >
                    ← Back to {isFromCrrPage ? 'CRR' : 'Main'}
                  </Button>
                  {(results || noDataFound || cn61ReportData) && (
                    <ClearResultsButton
                      variant='outlined'
                      onClick={clearResults}
                      data-testid='cn61-reims-page-clear-results-btn'
                    >
                      Clear Results
                    </ClearResultsButton>
                  )}
                </Box>
              </Box>

              {/* Incomplete Data Alert */}
              {showIncompleteDataAlert && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3,
                    '& .MuiAlert-message': {
                      fontSize: '14px',
                      fontWeight: 500
                    }
                  }}
                  onClose={() => setShowIncompleteDataAlert(false)}
                  data-testid="cn61-reims-page-incomplete-data-alert"
                >
                  The NO3 & CN56 Summary is incomplete and thus the CN61 REIMS/Interconnect cannot be generated. Please complete the NO3 & CN56 Summary (NET IPK/ADJ/Net %) and try it again.
                </Alert>
              )}

              {/* Main Filter Section - Only show if not from redirect */}
              {!isFromRedirect && (
                <>
                  {/* PPO Section */}
                  <FilterSection data-testid="cn61-reims-page-filter-section">
                  {/* PPO Dropdown and Direction Filter in one row */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2 }} data-testid="cn61-reims-page-ppo-direction-row">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="cn61-reims-page-ppo-container">
                      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }} data-testid="cn61-reims-page-ppo-label">
                        DO *
                      </Typography>
                      <SelectContainer data-testid="cn61-reims-page-ppo-select-container">
                        <FormControl size='small' fullWidth data-testid="cn61-reims-page-ppo-form-control">
                          <Select
                            value={filters.ppoValue}
                            onChange={(e) => handlePpoChange(e.target.value)}
                            displayEmpty
                            sx={{ minWidth: 160 }}
                            disabled={isPpoLoading}
                            data-testid="cn61-reims-page-ppo-select"
                          >
                            <MenuItem value='' data-testid="cn61-reims-page-ppo-default-option">
                              {isPpoLoading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="cn61-reims-page-ppo-loading">
                                  <CircularProgress size={16} data-testid="cn61-reims-page-ppo-loading-spinner" />
                                  <span data-testid="cn61-reims-page-ppo-loading-text">Loading...</span>
                                </Box>
                              ) : (
                                'All'
                              )}
                            </MenuItem>
                            {!isPpoLoading &&
                              !ppoError &&
                              ppoOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id} data-testid={`cn61-reims-page-ppo-option-${option.id}`}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      width: '100%',
                                    }}
                                    data-testid={`cn61-reims-page-ppo-option-content-${option.id}`}
                                  >
                                    <span data-testid={`cn61-reims-page-ppo-option-label-${option.id}`}>{option.label}</span>
                                    
                                  </Box>
                                </MenuItem>
                              ))}
                            {ppoError && <MenuItem disabled data-testid="cn61-reims-page-ppo-error">Error loading PPOs</MenuItem>}
                          </Select>
                        </FormControl>
                      </SelectContainer>
                    </Box>

                    {/* Direction Filter using Segmented Control */}
                    <StyledSegmentedControl data-testid="cn61-reims-page-direction-control">
                      {directionOptions.map((option) => (
                        <StyledSegmentButton
                          key={option.id}
                          active={filters.direction === option.id}
                          onClick={() => handleDirectionChange(option.id)}
                          sx={{
                            color: filters.direction === option.id ? '#ffffff' : '#00B6BA',
                            minWidth: '70px',
                          }}
                          data-testid={`cn61-reims-page-direction-${option.id}`}
                        >
                          {option.label}
                        </StyledSegmentButton>
                      ))}
                    </StyledSegmentedControl>
                  </Box>

                  {/* Divider */}
                  <Box
                    sx={{
                      height: '1px',
                      backgroundColor: '#E1E8ED',
                      mb: 2,
                    }}
                    data-testid="cn61-reims-page-divider-1"
                  />

                  {/* Year Selector */}
                  <Box sx={{ display: 'flex', gap: 2 }} data-testid="cn61-reims-page-year-section">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="cn61-reims-page-year-container">
                      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }} data-testid="cn61-reims-page-year-label">
                        Year: *
                      </Typography>
                      <SelectContainer data-testid="cn61-reims-page-year-select-container">
                        <FormControl size='small' fullWidth data-testid="cn61-reims-page-year-form-control">
                          <Select
                            value={filters.year}
                            onChange={(e) => handleYearChange(e.target.value)}
                            displayEmpty
                            sx={{ minWidth: 184 }}
                            data-testid="cn61-reims-page-year-select"
                          >
                            <MenuItem value='' data-testid="cn61-reims-page-year-default-option">Select Year</MenuItem>
                            {yearOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value} data-testid={`cn61-reims-page-year-option-${option.value}`}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </SelectContainer>
                    </Box>
                  </Box>

                  {/* Divider */}
                  <Box
                    sx={{
                      height: '1px',
                      backgroundColor: '#E1E8ED',
                      mb: 2,
                    }}
                    data-testid="cn61-reims-page-divider-2"
                  />

                  {/* CN61 REIMS Section */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      gap: 2,
                    }}
                    data-testid="cn61-reims-page-cn61-section"
                  >
                    <Box
                      sx={{
                        padding: 2,
                        minWidth: 0,
                      }}
                      data-testid="cn61-reims-page-cn61-content"
                    >
                      <SectionTitle data-testid="cn61-reims-page-cn61-title">CN61 Interconnect</SectionTitle>
                      <Typography
                        variant='body2'
                        sx={{
                          color: '#666',
                          mt: 1,
                          fontSize: '14px',
                          mb: 2,
                        }}
                        data-testid="cn61-reims-page-cn61-description"
                      >
                        Generate CN61 reports for REIMS and Interconnect data using
                        /cn61/cn61Crr/cn61Reim endpoint
                      </Typography>
                      <Button
                        variant='outlined'
                        size='small'
                        onClick={handleEnterTdRates}
                        disabled={isGeneratingReport || !filters.year || !filters.ppoValue}
                        sx={{
                          borderColor: '#00ACE7',
                          color: '#00ACE7',
                          fontSize: '12px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          padding: '6px 16px',
                          mb: 2,
                          '&:hover': {
                            borderColor: '#0095C7',
                            backgroundColor: '#E3F7FE',
                          },
                          '&:disabled': {
                            borderColor: '#ccc',
                            color: '#ccc',
                          },
                        }}
                        data-testid="cn61-reims-page-enter-td-rates-btn"
                      >
                        {isGeneratingReport ? <CircularProgress size={14} data-testid="cn61-reims-page-enter-td-rates-spinner" /> : 'Enter TD Rates'}
                      </Button>
                    </Box>
                  </Box>

                  {/* Currency Input and Action Buttons - Only show after TD rates data is loaded */}
                  {hasTdRatesData && (
                    <Box sx={{ mb: 3 }} data-testid="cn61-reims-page-action-buttons">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <TextField
                          label="Currency"
                          value={currency}
                          onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                          size="small"
                          sx={{ minWidth: 120 }}
                          inputProps={{
                            maxLength: 3,
                            style: { textTransform: 'uppercase' }
                          }}
                          data-testid='cn61-reims-page-currency-input'
                        />
                      </Box>
                      <ButtonContainer>
                        <ExportButton
                          variant='contained'
                          onClick={() => handleExport('html')}
                          disabled={isGeneratingReport}
                          data-testid="cn61-reims-page-export-html-btn"
                        >
                          {isGeneratingReport ? (
                            <CircularProgress size={16} data-testid="cn61-reims-page-export-html-spinner" />
                          ) : (
                            'HTML'
                          )}
                        </ExportButton>
                        <ExportButton
                          variant='contained'
                          onClick={() => handleExport('pdf')}
                          disabled={isGeneratingReport}
                          data-testid="cn61-reims-page-export-pdf-btn"
                        >
                          {isGeneratingReport ? <CircularProgress size={16} data-testid="cn61-reims-page-export-pdf-spinner" /> : 'PDF'}
                        </ExportButton>
                      </ButtonContainer>
                    </Box>
                  )}
                </FilterSection>
              </>
              )}

              {/* Currency Input and Export Buttons - Show when data is loaded from redirect */}
              {isFromRedirect && hasTdRatesData && (
                <Box sx={{ mb: 3 }} data-testid="cn61-reims-page-redirect-export-buttons">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <TextField
                      label="Currency"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                      size="small"
                      sx={{ minWidth: 120 }}
                      inputProps={{
                        maxLength: 3,
                        style: { textTransform: 'uppercase' }
                      }}
                      data-testid='cn61-reims-page-redirect-currency-input'
                    />
                  </Box>
                  <ButtonContainer data-testid="cn61-reims-page-redirect-button-container">
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('html')}
                      disabled={isGeneratingReport}
                      data-testid="cn61-reims-page-redirect-export-html-btn"
                    >
                      {isGeneratingReport ? (
                        <CircularProgress size={16} data-testid="cn61-reims-page-redirect-export-html-spinner" />
                      ) : (
                        'HTML'
                      )}
                    </ExportButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('pdf')}
                      disabled={isGeneratingReport}
                      data-testid="cn61-reims-page-redirect-export-pdf-btn"
                    >
                      {isGeneratingReport ? <CircularProgress size={16} data-testid="cn61-reims-page-redirect-export-pdf-spinner" /> : 'PDF'}
                    </ExportButton>
                    {/* <ExportButton
                      variant='contained'
                      onClick={() => handleExport('xls')}
                      disabled={isGeneratingReport}
                      data-testid="cn61-reims-page-redirect-export-xls-btn"
                    >
                      {isGeneratingReport ? <CircularProgress size={16} data-testid="cn61-reims-page-redirect-export-xls-spinner" /> : 'XLS'}
                    </ExportButton> */}
                  </ButtonContainer>
                </Box>
              )}

              {/* Retry Button - Show when from redirect and no data and retries available */}
              {isFromRedirect && !hasTdRatesData && !isGeneratingReport && retryCount < 3 && hasAttemptedLoad && (
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }} data-testid="cn61-reims-page-retry-section">
                  <Button
                    variant='outlined'
                    onClick={handleRetry}
                    sx={{
                      borderColor: '#00ACE7',
                      color: '#00ACE7',
                      '&:hover': {
                        borderColor: '#0095C7',
                        backgroundColor: '#E3F7FE',
                      },
                    }}
                    data-testid="cn61-reims-page-retry-btn"
                  >
                    Retry Loading CN61 Data ({retryCount}/3)
                  </Button>
                </Box>
              )}

              {/* Loading indicator when from redirect */}
              {isFromRedirect && !hasTdRatesData && isGeneratingReport && (
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }} data-testid="cn61-reims-page-loading-section">
                  <CircularProgress size={20} data-testid="cn61-reims-page-loading-spinner" />
                  <Typography data-testid="cn61-reims-page-loading-text">Loading CN61 REIMS data...</Typography>
                </Box>
              )}

              {/* Error Section */}
              {cn61ReimError && (
                <Alert severity='error' sx={{ mb: 3 }} data-testid="cn61-reims-page-error-alert">
                  Failed to load report data. Please try again.
                </Alert>
              )}

              {/* No Data Found Section */}
              {noDataFound && (
                <NoDataFound
                  title='No Data Available'
                  message='No CN61 REIMS/Interconnect data was found for the selected criteria. Please try adjusting your filters or selecting a different year.'
                  onClearResults={clearResults}
                  onResetFilters={handleReset}
                  data-testid="cn61-reims-page-no-data-found"
                />
              )}

              {/* Results Section - Show tables when results exist */}
              {(results || cn61ReportData) && !noDataFound && (
                <Box sx={{ mt: 6 }}>
                  <Box
                    sx={{
                      borderBottom: '2px solid #E1E8ED',
                      pb: 2,
                      mb: 4,
                    }}
                  ></Box>

                  {/* Results content will go here */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {/* CN61 Data from REIM endpoint - Show editable table */}
                    {cn61InputData && cn61InputData.request && (
                      <Box>
                        <CN61Table 
                          data={cn61InputData} 
                          onDataChange={handleCN61DataChange}
                          onError={showSnackbar}
                          onSuccess={handleCN61Success}
                          currentUserPpo={userInfo?.designatedOperator}
                        />
                      </Box>
                    )}

                    {/* CN61 HTML Report Tables */}
                    {cn61ReportData && cn61ReportData.length > 0 && (
                      <Box>
                        {cn61ReportData.map((directionData, index) => (
                          <Box key={index} sx={{ mb: 4 }}>
                            <CN61HtmlReportTable 
                              data={directionData.data} 
                              direction={directionData.direction}
                              title={`CN61 ${directionData.direction.charAt(0).toUpperCase() + directionData.direction.slice(1)} Report`}
                            />
                          </Box>
                        ))}
                        {/* Single Balance Section after all tables */}
                        {(() => {
                          const reportBalance = cn61ReportData?.[0]?.balance ?? cn61ReportData?.[1]?.balance ?? null;
                          if (reportBalance === null || reportBalance === undefined) return null;
                          return (
                            <Box sx={{ mt: 3 }} data-testid="cn61-html-report-balance">
                              <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem', color: '#1976d2', backgroundColor: '#f5f5f5' }}>
                                        {userInfo?.designatedOperator && filters.ppoValue
                                          ? `Balance from ${userInfo.designatedOperator} to ${filters.ppoValue}`
                                          : 'Balance'}
                                      </TableCell>
                                      <TableCell sx={{ textAlign: 'right', fontWeight: 700, fontSize: '1rem', color: 'black', backgroundColor: '#f5f5f5' }}>
                                        {String(reportBalance)}
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                </Table>
                              </TableContainer>
                            </Box>
                          );
                        })()}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid='cn61-reims-page-snackbar'
      />
    </Box>
  );
}

export default CN61ReimsPage;
